import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useProjects } from '../../../context/ProjectContext';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { useProjectActionCable } from '../../../hooks/useProjectActionCable';
import { modelServiceUpload } from '../../../utils/modelService';
import {
  ArrowLeft,
  Plus,
  MessageSquare,
  FileText,
  Upload,
  Trash2,
  Loader2,
  AlertCircle,
  MoreVertical,
  Edit2,
  FolderInput,
  FolderMinus,
  UserPlus,
  Users,
  MessageCircle,
  FolderSymlink
} from 'lucide-react';
import {
  RenameChatModal,
  MoveToProjectModal,
  RemoveFromProjectModal,
  DocumentActionModal
} from '../../common/modals/ChatActionsModals';
import {
  InviteMemberModal,
  ProjectMembersModal
} from '../../common/modals';
import TeamChatDrawer from '../../team-chat/TeamChatDrawer';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { projects, selectProject } = useProjects();
  const { theme } = useTheme();
  const { canAccessTeamChat } = useAuth();

  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [chats, setChats] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [error, setError] = useState(null);

  // Chat management states
  const [activeChatMenu, setActiveChatMenu] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);

  // Document action states
  const [showDocActionModal, setShowDocActionModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Collaboration states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showTeamChat, setShowTeamChat] = useState(false);

  // Real-time event handler
  const handleRealtimeEvent = useCallback((data) => {
    console.log('📡 Real-time event in ProjectDetail:', data);
    console.log('📡 Current project ID:', id, 'Event project ID:', data.project_id);
    console.log('📡 Current chats state:', chats);

    // Filter by project_id - ignore events from other projects
    if (data.project_id && parseInt(id) !== data.project_id) {
      console.log('⏭️  Ignoring event for different project');
      return;
    }

    switch(data.type) {
      case 'member_joined':
        toast.success(`${data.user.username} joined the project`);
        // Optionally refresh members list
        break;

      case 'member_removed':
        toast(`${data.user.username} left the project`);
        break;

      case 'chat_created':
        toast(`${data.creator.username} started a new chat: ${data.chat.title}`);
        // Add the new chat to the list
        setChats(prevChats => [data.chat, ...prevChats]);
        break;

      case 'chat_updated':
        // Update the chat in the list
        setChats(prevChats =>
          prevChats.map(chat =>
            chat.id === data.chat.id ? { ...chat, ...data.chat } : chat
          )
        );
        break;

      case 'chat_deleted':
      case 'chat_removed':
        // Remove the chat from the list
        setChats(prevChats => prevChats.filter(chat => chat.id !== data.chat_id));
        if (data.type === 'chat_deleted') {
          toast('A chat was deleted');
        }
        break;

      case 'message_created':
        // Update chat's updated_at to show recent activity
        setChats(prevChats =>
          prevChats.map(chat =>
            chat.id === data.chat_id
              ? { ...chat, updated_at: new Date().toISOString(), message_count: (chat.message_count || 0) + 1 }
              : chat
          ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        );
        break;

      case 'document_uploaded':
        toast(`${data.document.uploaded_by} uploaded ${data.document.filename}`);
        // Add document to list if we have the data
        if (data.document && activeTab === 'documents') {
          setDocuments(prevDocs => [data.document, ...prevDocs]);
        }
        break;

      case 'invitation_sent':
        // Silent update, no toast needed
        break;

      default:
        console.log('Unknown event type:', data.type);
    }
  }, [activeTab]);

  // Connect to Action Cable for real-time updates (only for collaborative projects)
  const { isConnected, connectionError } = useProjectActionCable(
    project?.is_collaborative ? id : null,
    handleRealtimeEvent
  );

  // Fetch project details (always from API to get context_size stats)
  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      try {
        const { apiGet } = await import('../../../utils/api');
        const response = await apiGet(`/projects/${id}`);
        if (response.ok) {
          const data = await response.json();
          setProject(data.project);
        } else {
          setError('Project not found');
        }
      } catch (err) {
        console.error('Failed to fetch project:', err);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  // Fetch chats for project
  useEffect(() => {
    if (!project) return;

    const fetchChats = async () => {
      setLoadingChats(true);
      try {
        const { apiGet } = await import('../../../utils/api');
        const response = await apiGet(`/chats?project_id=${project.id}`);
        if (response.ok) {
          const data = await response.json();
          setChats(data.chats || []);
        }
      } catch (err) {
        console.error('Failed to fetch chats:', err);
      } finally {
        setLoadingChats(false);
      }
    };

    fetchChats();
  }, [project]);

  // Fetch documents when Documents tab is clicked
  const fetchDocuments = async () => {
    if (!project) return;

    try {
      const { apiGet } = await import('../../../utils/api');
      const response = await apiGet(`/projects/${project.id}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  const handleUploadDocument = async (file) => {
    setUploadingDocument(true);
    setError(null);

    try {
      // Step 1: Process file through model service to extract content
      const processFormData = new FormData();
      processFormData.append('files', file);

      const processResponse = await modelServiceUpload('/process-files', processFormData);

      if (!processResponse.ok) {
        throw new Error('Failed to process document');
      }

      const processedData = await processResponse.json();
      const processedContent = processedData.files?.[0]?.content || processedData.files?.[0]?.text || '';

      // Step 2: Upload file with processed content to Rails API
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('processed_content', processedContent);

      const { apiUpload } = await import('../../../utils/api');
      const response = await apiUpload(`/projects/${project.id}/upload_document`, uploadFormData);

      if (response.ok) {
        await fetchDocuments();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to upload document');
      }
    } catch (err) {
      console.error('Failed to upload document:', err);
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      const { apiDelete } = await import('../../../utils/api');
      const response = await apiDelete(`/projects/${project.id}/documents/${documentId}`);

      if (response.ok) {
        await fetchDocuments();
      } else {
        setError('Failed to delete document');
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      setError('Failed to delete document');
    }
  };

  const handleMoveDocument = async (targetProjectId) => {
    if (!selectedDoc) return;
    try {
      const { apiPost } = await import('../../../utils/api');
      const response = await apiPost(`/projects/${project.id}/documents/${selectedDoc.id}/move`, { target_project_id: targetProjectId });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setDocuments(prev => prev.filter(d => d.id !== selectedDoc.id));
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to move document');
      }
    } catch (err) {
      console.error('Failed to move document:', err);
      toast.error('Failed to move document');
    }
    setShowDocActionModal(false);
    setSelectedDoc(null);
  };

  const handleCopyDocument = async (targetProjectId) => {
    if (!selectedDoc) return;
    try {
      const { apiPost } = await import('../../../utils/api');
      const response = await apiPost(`/projects/${project.id}/documents/${selectedDoc.id}/copy`, { target_project_id: targetProjectId });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // If copied to same project, refresh the list
        if (targetProjectId === project.id) {
          await fetchDocuments();
        }
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to copy document');
      }
    } catch (err) {
      console.error('Failed to copy document:', err);
      toast.error('Failed to copy document');
    }
    setShowDocActionModal(false);
    setSelectedDoc(null);
  };

  // Drag and drop handlers with counter to prevent flickering
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setDragCounter(prev => prev + 1);

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setDragCounter(prev => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragging(false);
      }
      return newCounter;
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Upload all dropped files
      Array.from(files).forEach(file => {
        handleUploadDocument(file);
      });
    }
  };

  const handleStartNewChat = () => {
    selectProject(project);
    localStorage.removeItem('unified_current_chat_id');
    localStorage.removeItem('unified_current_session');
    localStorage.removeItem('unified_rag_session_id');
    navigate('/dashboard/unified-chat');
  };

  const handleOpenChat = (chat) => {
    selectProject(project);
    localStorage.setItem('unified_current_chat_id', chat.id.toString());
    navigate('/dashboard/unified-chat');
  };

  // Rename chat
  const handleRenameChat = async (newTitle) => {
    if (!selectedChat) return;

    try {
      const { apiPatch } = await import('../../../utils/api');
      const response = await apiPatch(`/chats/${selectedChat.id}`, {
        chat: { title: newTitle }
      });

      if (response.ok) {
        setChats(prevChats =>
          prevChats.map(c =>
            c.id === selectedChat.id ? { ...c, title: newTitle } : c
          )
        );
        setShowRenameModal(false);
        setSelectedChat(null);
      } else {
        setError('Failed to rename chat');
      }
    } catch (err) {
      console.error('Failed to rename chat:', err);
      setError('Failed to rename chat');
    }
  };

  // Move chat to another project
  const handleMoveChat = async (targetProjectId) => {
    if (!selectedChat) return;

    try {
      const { apiPatch } = await import('../../../utils/api');
      const response = await apiPatch(`/chats/${selectedChat.id}`, {
        chat: { project_id: targetProjectId }
      });

      if (response.ok) {
        setChats(prevChats => prevChats.filter(c => c.id !== selectedChat.id));
        setShowMoveModal(false);
        setSelectedChat(null);
      } else {
        setError('Failed to move chat');
      }
    } catch (err) {
      console.error('Failed to move chat:', err);
      setError('Failed to move chat');
    }
  };

  // Remove chat from project
  const handleRemoveFromProject = async () => {
    if (!selectedChat) return;

    try {
      const { apiPatch } = await import('../../../utils/api');
      const response = await apiPatch(`/chats/${selectedChat.id}`, {
        chat: { project_id: null }
      });

      if (response.ok) {
        setChats(prevChats => prevChats.filter(c => c.id !== selectedChat.id));
        setShowRemoveModal(false);
        setSelectedChat(null);
      } else {
        setError('Failed to remove chat from project');
      }
    } catch (err) {
      console.error('Failed to remove chat:', err);
      setError('Failed to remove chat from project');
    }
  };

  if (loading) {
    return (
      <div className={`flex-1 flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className={`flex-1 flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-lg font-rasa">{error || 'Project not found'}</p>
          <button
            onClick={() => navigate('/dashboard/projects')}
            className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-rasa"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col overflow-hidden ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className="max-w-6xl mx-auto p-6 w-full flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Header */}
        <div className="mb-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/dashboard/projects')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors font-rasa ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Projects
            </button>

            {/* Action Buttons - Top Right */}
            <div className="flex items-center gap-3">
              {canAccessTeamChat() && (
                <button
                  onClick={() => setShowTeamChat(true)}
                  className={`px-4 py-2 rounded-lg transition-colors font-rasa flex items-center gap-2 ${
                    theme === 'dark'
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  <MessageCircle className="w-4 h-4" />
                  Team Chat
                </button>
              )}

              {/* Show Members button only for collaborative projects */}
              {project.is_collaborative && (
                <button
                  onClick={() => setShowMembersModal(true)}
                  className={`px-4 py-2 rounded-lg transition-colors font-rasa flex items-center gap-2 ${
                    theme === 'dark'
                      ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Members
                </button>
              )}

              <button
                onClick={() => setShowInviteModal(true)}
                className={`px-4 py-2 rounded-lg transition-colors font-rasa flex items-center gap-2 ${
                  theme === 'dark'
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-purple-500 hover:bg-purple-600 text-white'
                }`}
              >
                <UserPlus className="w-4 h-4" />
                Invite
              </button>

              <button
                onClick={handleStartNewChat}
                className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors font-rasa flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: project.color }}
            />
            <h1 className={`text-3xl font-dm-serif ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {project.name}
            </h1>
          </div>

          {project.description && (
            <p className={`font-rasa ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {project.description}
            </p>
          )}

          {/* Connection Status - Only show for collaborative projects */}
          {project?.is_collaborative && (
            <div className="flex items-center gap-2 mt-3">
              {isConnected ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className={`text-xs font-rasa ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    Live
                  </span>
                </>
              ) : connectionError ? (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className={`text-xs font-rasa ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    Offline
                  </span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                  <span className={`text-xs font-rasa ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    Connecting...
                  </span>
                </>
              )}
            </div>
          )}

          {/* Context Size Warning */}
          {project.context_size && project.context_size.level !== 'healthy' && (
            <div className={`mt-3 px-4 py-3 rounded-lg flex items-start gap-3 ${
              project.context_size.level === 'critical'
                ? theme === 'dark' ? 'bg-red-900/30 border border-red-800' : 'bg-red-50 border border-red-200'
                : theme === 'dark' ? 'bg-orange-900/30 border border-orange-800' : 'bg-orange-50 border border-orange-200'
            }`}>
              <AlertCircle className={`w-4 h-4 mt-0.5 shrink-0 ${
                project.context_size.level === 'critical' ? 'text-red-500' : 'text-orange-500'
              }`} />
              <div>
                <p className={`text-sm font-medium font-rasa ${
                  project.context_size.level === 'critical'
                    ? theme === 'dark' ? 'text-red-300' : 'text-red-800'
                    : theme === 'dark' ? 'text-orange-300' : 'text-orange-800'
                }`}>
                  {project.context_size.level === 'critical'
                    ? 'Project context is very large'
                    : 'Project context is growing large'
                  }
                </p>
                <p className={`text-xs font-rasa mt-0.5 ${
                  project.context_size.level === 'critical'
                    ? theme === 'dark' ? 'text-red-400' : 'text-red-600'
                    : theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
                }`}>
                  {Math.round(project.context_size.total_bytes / 1024)}KB across {project.context_size.document_count} document{project.context_size.document_count !== 1 ? 's' : ''} and {project.context_size.memory_count} memor{project.context_size.memory_count !== 1 ? 'ies' : 'y'}.
                  {project.context_size.level === 'critical'
                    ? ' AI responses may lose accuracy. Consider archiving this project and starting a new one, or removing outdated documents.'
                    : ' Consider removing outdated documents to keep AI responses accurate.'
                  }
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className={`border-b mb-6 flex-shrink-0 ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-3 font-rasa transition-colors ${
                activeTab === 'overview'
                  ? theme === 'dark'
                    ? 'border-b-2 border-blue-500 text-blue-400'
                    : 'border-b-2 border-blue-500 text-blue-600'
                  : theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Conversations
            </button>
            <button
              onClick={() => {
                setActiveTab('documents');
                fetchDocuments();
              }}
              className={`pb-3 font-rasa transition-colors ${
                activeTab === 'documents'
                  ? theme === 'dark'
                    ? 'border-b-2 border-blue-500 text-blue-400'
                    : 'border-b-2 border-blue-500 text-blue-600'
                  : theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Documents
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="overflow-y-auto max-h-[calc(100vh-300px)] sm:max-h-[calc(100vh-320px)] md:max-h-[calc(100vh-350px)]">
            {loadingChats ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : chats.length === 0 ? (
              <div className={`text-center py-16 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="font-rasa text-lg mb-2">No conversations yet</p>
                <p className="text-sm font-rasa">Click "New Chat" above to start your first conversation in this project</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {chats.map((chat, chatIndex) => (
                  <div
                    key={chat.id}
                    className={`relative p-4 rounded-lg border transition-all ${
                      theme === 'dark'
                        ? 'border-gray-700 bg-gray-800 hover:bg-gray-750 hover:border-gray-600'
                        : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handleOpenChat(chat)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <h3 className={`font-rasa font-medium mb-1 truncate ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {chat.title || 'Untitled Chat'}
                        </h3>
                        {chat.preview && (
                          <p className={`text-sm font-rasa line-clamp-2 mb-2 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {chat.preview}
                          </p>
                        )}
                        <div className={`flex items-center gap-3 text-xs font-rasa ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          <span>{new Date(chat.updated_at).toLocaleDateString()}</span>
                          <span>•</span>
                          <span>{chat.message_count || 0} messages</span>
                        </div>
                      </button>

                      {/* Menu button */}
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveChatMenu(activeChatMenu === chat.id ? null : chat.id);
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                          }`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {/* Dropdown menu */}
                        {activeChatMenu === chat.id && (
                          <div className={`absolute right-0 w-48 rounded-lg shadow-lg border z-10 ${
                            chatIndex >= chats.length - 2 ? 'bottom-full mb-1' : 'top-full mt-1'
                          } ${
                            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                          }`}>
                            <button
                              onClick={() => {
                                setSelectedChat(chat);
                                setShowRenameModal(true);
                                setActiveChatMenu(null);
                              }}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-rasa transition-colors ${
                                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                              }`}
                            >
                              <Edit2 className="w-4 h-4" />
                              Rename
                            </button>
                            <button
                              onClick={() => {
                                setSelectedChat(chat);
                                setShowMoveModal(true);
                                setActiveChatMenu(null);
                              }}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-rasa transition-colors ${
                                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                              }`}
                            >
                              <FolderInput className="w-4 h-4" />
                              Move to Project
                            </button>
                            <button
                              onClick={() => {
                                setSelectedChat(chat);
                                setShowRemoveModal(true);
                                setActiveChatMenu(null);
                              }}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-rasa transition-colors ${
                                theme === 'dark' ? 'hover:bg-gray-700 text-orange-400' : 'hover:bg-gray-50 text-orange-600'
                              }`}
                            >
                              <FolderMinus className="w-4 h-4" />
                              Remove from Project
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div
            className="flex-1 min-h-0 overflow-y-auto"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Upload Button */}
            <div className="mb-6 flex items-center gap-3">
              <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors font-rasa ${
                uploadingDocument
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}>
                <Upload className="w-4 h-4" />
                <span>{uploadingDocument ? 'Uploading...' : 'Upload Document'}</span>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.md"
                  multiple
                  disabled={uploadingDocument}
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      // Upload all selected files
                      Array.from(files).forEach(file => {
                        handleUploadDocument(file);
                      });
                      e.target.value = '';
                    }
                  }}
                />
              </label>
              <span className={`text-xs font-rasa ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                or drag and drop files here
              </span>
            </div>

            {/* Drag overlay */}
            {isDragging && (
              <div className={`mb-6 p-8 border-2 border-dashed rounded-lg text-center ${
                theme === 'dark'
                  ? 'border-purple-500 bg-purple-900/20'
                  : 'border-purple-500 bg-purple-50'
              }`}>
                <Upload className={`w-8 h-8 mx-auto mb-2 ${
                  theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                }`} />
                <p className={`font-rasa ${
                  theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                }`}>
                  Drop files to upload
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 px-4 py-3 rounded-lg bg-red-500 bg-opacity-10 border border-red-500 text-red-500 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Documents List */}
            {documents.length === 0 ? (
              <div className={`text-center py-12 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-rasa">No documents yet</p>
                <p className="text-sm font-rasa mt-1">Documents uploaded here will be available to all chats in this project</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {documents.map(doc => (
                  <div
                    key={doc.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      theme === 'dark'
                        ? 'border-gray-700 bg-gray-800 hover:bg-gray-750'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                      }`}>
                        <FileText className={`w-5 h-5 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-rasa font-medium truncate ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {doc.filename}
                        </p>
                        <p className={`text-xs font-rasa ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : ''}
                          {doc.file_size && doc.content_size ? ' • ' : ''}
                          {doc.content_size ? `${doc.content_size.toLocaleString()} chars` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setSelectedDoc(doc);
                          setShowDocActionModal(true);
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:bg-gray-700'
                            : 'text-gray-500 hover:bg-gray-100'
                        }`}
                        title="Move or copy to another project"
                      >
                        <FolderSymlink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          theme === 'dark'
                            ? 'text-red-400 hover:bg-red-900/20'
                            : 'text-red-500 hover:bg-red-50'
                        }`}
                        title="Delete document"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Team Chat Drawer */}
      <TeamChatDrawer isOpen={showTeamChat} onClose={() => setShowTeamChat(false)} />

      {/* Modals */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        projectId={project?.id}
        onInviteSent={() => {
          // Optionally refresh members list
        }}
      />

      <ProjectMembersModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        projectId={project?.id}
        isOwner={project?.is_owner}
        currentUserRole={project?.role}
        onInviteClick={() => {
          setShowMembersModal(false);
          setShowInviteModal(true);
        }}
      />

      <RenameChatModal
        isOpen={showRenameModal}
        onClose={() => {
          setShowRenameModal(false);
          setSelectedChat(null);
        }}
        chatTitle={selectedChat?.title || ''}
        onRename={handleRenameChat}
      />

      <MoveToProjectModal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setSelectedChat(null);
        }}
        projects={projects}
        currentProjectId={project?.id}
        onMove={handleMoveChat}
      />

      <RemoveFromProjectModal
        isOpen={showRemoveModal}
        onClose={() => {
          setShowRemoveModal(false);
          setSelectedChat(null);
        }}
        chatTitle={selectedChat?.title || ''}
        onConfirm={handleRemoveFromProject}
      />

      <DocumentActionModal
        isOpen={showDocActionModal}
        onClose={() => {
          setShowDocActionModal(false);
          setSelectedDoc(null);
        }}
        projects={projects}
        currentProjectId={project?.id}
        documentName={selectedDoc?.filename}
        onMove={handleMoveDocument}
        onCopy={handleCopyDocument}
      />

      {/* Click outside to close menu */}
      {activeChatMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveChatMenu(null)}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
